import React, { useEffect, useRef, useState } from "react";
import { Pause } from "lucide-react";
import { tvScenes, brokerPnl, tvControlGet } from "../../lib/api";
import { TvMission } from "./TvMission";
import { TvNews } from "./TvNews";
import { TvBusiness } from "./TvBusiness";
import { TvMadMoney } from "./MadMoney";
import { TvChannelVideos, unseenVideos } from "./ChannelVideos";
import { TvNewsroom } from "./TvNewsroom";
import { TvGlobe } from "./TvGlobe";
import { TvLiving } from "./TvLiving";
import { TvMoney } from "./TvMoney";
import { TvReminders } from "./TvReminders";
import NexusIntel from "../NexusIntel";
import { IraScene } from "./IraScene";
import { FloorPipeline } from "./FloorPipeline";
import { TvHouseTeam } from "./TvHouseTeam";
import { TvVideoDigest } from "./TvVideoDigest";
import { TvDigestRecord } from "./TvDigestRecord";
import { TvBrokerBoard } from "./TvBrokerBoard";
import { TvWhaleFlow } from "./TvWhaleFlow";
import { TvEarningsFlow } from "./TvEarningsFlow";
import { TvFloorDay } from "./TvFloorDay";

const TvIntel = ({ userId }) => <div className="h-full overflow-y-auto rounded-3xl" data-testid="tv-scene-intel"><NexusIntel session={{ user_id: userId }} embedded /></div>;

const SCENES = [
  { id: "mission", label: "MISSION", C: TvMission },
  { id: "team", label: "HOUSE TEAM", C: TvHouseTeam },
  { id: "floor", label: "FLOOR", C: FloorPipeline, extra: true },
  { id: "floor-day", label: "FLOOR · TODAY", C: TvFloorDay },
  { id: "news", label: "NEWS", C: TvNews },
  { id: "business", label: "BUSINESS", C: TvBusiness },
  { id: "madmoney", label: "MAD MONEY", C: TvMadMoney },
  { id: "channels", label: "CHANNELS", C: TvChannelVideos },
  { id: "record", label: "TRACK RECORD", C: TvDigestRecord },
  { id: "brokers", label: "BROKERS", C: TvBrokerBoard },
  { id: "whales", label: "WHALE FLOW", C: TvWhaleFlow },
  { id: "earnings", label: "EARNINGS FLOW", C: TvEarningsFlow },
  { id: "newsroom", label: "NEWSROOM", C: TvNewsroom },
  { id: "globe", label: "GLOBE", C: TvGlobe },
  { id: "living", label: "LIVING", C: TvLiving },
  { id: "ira", label: "IRA", C: IraScene },
  { id: "reminders", label: "REMINDERS", C: TvReminders, extra: true },
  { id: "intel", label: "INTEL", C: TvIntel, extra: true },
];
const DWELL = 60000;
const DIGEST_EVERY = 10, DIGEST_MS = 30000;
const FORCED = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("scene") : null;

export const NexusScenes = ({ userId, onPnl, onVariant, only, screen }) => {
  const variantRef = useRef(onVariant); variantRef.current = onVariant;
  const [d, setD] = useState({});
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [money, setMoney] = useState(null);
  const [digest, setDigest] = useState(false);
  const advRef = useRef(0);
  const dRef = useRef(d); dRef.current = d;
  const scenes = SCENES.filter((sc) => (only ? only.includes(sc.id) : !sc.extra) && (sc.id !== "madmoney" || d.madmoney) && (sc.id !== "channels" || unseenVideos(d).length > 0) && (sc.id !== "record" || (d.digest_record || []).length > 0) && (sc.id !== "ira" || d.ira) && (sc.id !== "whales" || d.uw_flow) && (sc.id !== "earnings" || d.earnings_flow));
  const scenesRef = useRef(scenes); scenesRef.current = scenes;
  const mmDayRef = useRef("");
  useEffect(() => {
    if (!d.madmoney) return;
    const et = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const day = et.toDateString();
    if (et.getHours() >= 5 && et.getHours() < 11 && mmDayRef.current !== day) {
      mmDayRef.current = day;
      setIdx(scenesRef.current.findIndex((sc) => sc.id === "madmoney"));
    }
  }, [d.madmoney]);

  useEffect(() => {
    const load = () => tvScenes(userId).then(setD).catch(() => {});
    load(); const t = setInterval(load, 300000); return () => clearInterval(t);
  }, [userId]);
  useEffect(() => {
    const m = () => brokerPnl(userId).then((p) => onPnl && onPnl((p.day?.realized || 0) + (p.open?.unrealized || 0))).catch(() => {});
    m(); const t = setInterval(m, 300000); return () => clearInterval(t);
  }, [userId, onPnl]);
  useEffect(() => {
    let v = -1;
    const poll = () => tvControlGet(userId).then((c) => {
      if (c.v === v) return;
      v = c.v;
      if (screen && (c.screen || "a") !== screen) return;
      setMoney(c.money || null);
      setPaused(!!c.paused);
      if (c.variant && variantRef.current) variantRef.current(c.variant);
      const k = scenesRef.current.findIndex((s) => s.id === c.scene);
      if (k >= 0) setIdx(k);
    }).catch(() => {});
    poll(); const t = setInterval(poll, 1500); return () => clearInterval(t);
  }, [userId, screen]);
  useEffect(() => {
    const go = (e) => { const k = scenesRef.current.findIndex((s) => s.id === e.detail); if (k >= 0) setIdx(k); };
    window.addEventListener("nexus-scene", go);
    return () => window.removeEventListener("nexus-scene", go);
  }, []);
  const hasIra = scenes.some((sc) => sc.id === "ira");
  useEffect(() => {
    if (!FORCED) return;
    const k = scenesRef.current.findIndex((sc) => sc.id === FORCED);
    if (k >= 0) setIdx(k);
  }, [scenes.length]);
  useEffect(() => {
    if (paused || money || FORCED) return;
    if (digest) { const t = setTimeout(() => setDigest(false), DIGEST_MS); return () => clearTimeout(t); }
    const onIra = scenesRef.current[idx]?.id === "ira";
    const t = setTimeout(() => {
      advRef.current += 1;
      if (advRef.current >= DIGEST_EVERY && dRef.current.video_digest) { advRef.current = 0; setDigest(true); }
      setIdx((i) => {
      const L = scenesRef.current;
      let n = (i + 1) % L.length;
      if (L[n]?.id === "ira" && L.length > 1) n = (n + 1) % L.length;
      return n;
      });
    }, onIra ? 40000 : DWELL);
    return () => clearTimeout(t);
  }, [paused, money, idx, digest]);
  useEffect(() => {
    if (!hasIra || FORCED) return;
    const t = setInterval(() => { if (!paused && !money) setIdx(scenesRef.current.findIndex((sc) => sc.id === "ira")); }, 300000);
    return () => clearInterval(t);
  }, [hasIra, paused, money]);

  const S = scenes[idx % scenes.length];
  const btn = "nx-mono text-sm tracking-widest font-bold px-4 py-2 rounded-full transition-colors";
  return (
    <div className="h-full flex flex-col gap-3" data-testid="tv-scenes">
      <div className="flex items-center gap-2 shrink-0 flex-nowrap overflow-hidden" data-testid="tv-scene-nav">
        {scenes.map((sc, i) => (
          <button key={sc.id} onClick={() => setIdx(i)} data-testid={`tv-scene-tab-${sc.id}`}
            className={`${btn} shrink-0 whitespace-nowrap ${i === idx ? "bg-[#00F0FF] text-[#030712]" : "text-cyan-200/50 border border-cyan-200/15"}`}>{sc.label}</button>
        ))}
        {paused && <span className="nx-mono text-sm tracking-widest text-amber-300/80 flex items-center gap-1 ml-2" data-testid="tv-scene-held"><Pause className="w-4 h-4" />HELD</span>}
      </div>
      <div className="flex-1 min-h-0 relative">
        {(digest || FORCED === "digest") && d.video_digest
          ? <div key="digest" className="h-full nx-fade-up"><TvVideoDigest d={d} /></div>
          : <div key={S.id} className="h-full nx-fade-up"><S.C d={d} userId={userId} /></div>}
        {money && <TvMoney view={money} userId={userId} />}
      </div>
    </div>
  );
};
